import { isNil } from 'lodash-es';

import { services } from 'services/base';
import {
  GetMainNodeService,
  IsSingleAppService,
  OnMainNodeChangeService,
  OnMainNodeChangeServiceCallback,
  OnSingleAppChangeService,
  OnSingleAppChangeServiceCallback,
  SetMainNodeService,
  SingleAppMode,
  UpdateSingleAppService,
  ViewportServiceState,
} from 'types';
import { getMainConfig, logger, registerStateServices } from 'utils';

const calcSingleApp = (appModules: object[]) => {
  const singleAppOnly = getMainConfig('singleAppOnly');
  const singleAppMode = getMainConfig('singleAppMode');
  if (process.env.NODE_ENV === 'development' && !isNil(singleAppOnly)) {
    logger.warn(
      [
        'Deprecated config, please use the `singleAppMode` config instead of `singleAppOnly`!',
        `You can set \`singleAppMode: '${singleAppOnly ? 'always' : 'never'}`,
      ].join('\n'),
    );
  }

  if (!singleAppMode) {
    return singleAppOnly || false;
  }

  if (singleAppMode === SingleAppMode.Auto) {
    return appModules.length <= 1;
  }

  return singleAppMode === SingleAppMode.Always;
};

const SERVICE_PREFIX = 'appFrame.viewport';
const ACTION_PREFIX = 'AppFrame:ViewPort';
const ACTION_SET_MAIN_NODE = `${ACTION_PREFIX}:SetSingleAppMode`;
const ACTION_UPDATE_SINGLE_APP_MODE = `${ACTION_PREFIX}:UpdateSingleAppMode`;

const initViewportServices = () => {
  const { registerService, executeAction, registerAction, clearAction } = services;

  const state: ViewportServiceState = {
    singleApp: false,
    mainNode: null,
    fullscreen: false,
  };

  // main node change actions and services
  registerAction(
    ACTION_SET_MAIN_NODE,
    (node: HTMLElement | null) => {
      state.mainNode = node;
    },
    { order: 0 },
  );
  registerService<GetMainNodeService>(`${SERVICE_PREFIX}.getMainNode`, () => state.mainNode);
  registerService<SetMainNodeService>(`${SERVICE_PREFIX}.setMainNode`, (node: HTMLElement | null) => {
    executeAction(ACTION_SET_MAIN_NODE, node);
  });
  registerService<OnMainNodeChangeService>(
    `${SERVICE_PREFIX}.onMainNodeChange`,
    (callback: OnMainNodeChangeServiceCallback) => {
      registerAction(ACTION_SET_MAIN_NODE, callback, { order: 1 });
      return () => clearAction(ACTION_SET_MAIN_NODE, callback);
    },
  );

  // single app change actions and services
  registerAction(
    ACTION_UPDATE_SINGLE_APP_MODE,
    (singleApp: boolean) => {
      state.singleApp = singleApp;
    },
    { order: 0 },
  );
  registerService<IsSingleAppService>(`${SERVICE_PREFIX}.isSingleApp`, () => state.singleApp);
  registerService<UpdateSingleAppService>(`${SERVICE_PREFIX}.updateSingleApp`, (appModules: object[]) => {
    executeAction(ACTION_UPDATE_SINGLE_APP_MODE, calcSingleApp(appModules));
  });
  registerService<OnSingleAppChangeService>(
    `${SERVICE_PREFIX}.onSingleAppChange`,
    (callback: OnSingleAppChangeServiceCallback) => {
      registerAction(ACTION_UPDATE_SINGLE_APP_MODE, callback, { order: 1 });
      return () => clearAction(ACTION_UPDATE_SINGLE_APP_MODE, callback);
    },
  );

  registerStateServices({
    state,
    stateKey: 'fullscreen',
    servicePrefix: SERVICE_PREFIX,
    initValue: false,
    serviceMethods: {
      get: 'isFullScreen',
      set: 'setFullScreen',
      onChange: 'onFullScreenChange',
    },
  });
};

export default initViewportServices;
